import { useForumType } from '@/components/hooks/useForumType';
import React, { useEffect, useState } from 'react';
import { Link } from '../../lib/reactRouterWrapper';
import { useLocation } from '../../lib/routeUtil';
import { userCanDo } from '../../lib/vulcan-users/permissions';
import { userCanEditUser, userGetDisplayName, userGetProfileUrl, userGetProfileUrlFromSlug } from "../../lib/collections/users/helpers";
import { userGetEditUrl } from '../../lib/vulcan-users/helpers';
import { DEFAULT_LOW_KARMA_THRESHOLD, POST_SORTING_MODES } from '../../lib/collections/posts/views'
import StarIcon from '@/lib/vendor/@material-ui/icons/src/Star'
import DescriptionIcon from '@/lib/vendor/@material-ui/icons/src/Description'
import MessageIcon from '@/lib/vendor/@material-ui/icons/src/Message'
import PencilIcon from '@/lib/vendor/@material-ui/icons/src/Create'
import classNames from 'classnames';
import { useCurrentUser } from '../common/withUser';
import {AnalyticsContext} from "../../lib/analyticsEvents";
import { hasEventsSetting, siteNameWithArticleSetting, taglineSetting, nofollowKarmaThreshold } from '@/lib/instanceSettings';
import { separatorBulletStyles } from '../common/SectionFooter';
import { getSortOrderOptions } from '../../lib/collections/posts/dropdownOptions';
import CopyToClipboard from 'react-copy-to-clipboard';
import { useMessages } from '../common/withMessages';
import CopyIcon from '@/lib/vendor/@material-ui/icons/src/FileCopy'
import { getUserStructuredData } from '../../../../app/users/[slug]/UsersSingle';
import { COMMENT_SORTING_MODES } from '@/lib/collections/comments/views';
import { useDialog } from '../common/withDialog';
import pick from 'lodash/pick';
import PostsListSettings, { postListSettingUrlParameterNames } from '../posts/PostsListSettings';
import { TooltipSpan } from '../common/FMTooltip';
import MetaInfo from "../common/MetaInfo";
import OmegaIcon from "../icons/OmegaIcon";
import Error404 from "../common/Error404";
import NewFeedButton from "../rss/NewFeedButton";
import NewDialogueDialog from "../posts/NewDialogueDialog";
import SequencesGridWrapper from "../sequences/SequencesGridWrapper";
import DraftsList from "../posts/DraftsList";
import PostsList2 from "../posts/PostsList2";
import LocalGroupsList from "../localGroups/LocalGroupsList";
import LWTooltip from "../common/LWTooltip";
import ProfileShortform from "../shortform/ProfileShortform";
import RecentComments from "../comments/RecentComments";
import SunshineNewUsersProfileInfo from "../sunshineDashboard/SunshineNewUsersProfileInfo";
import SingleColumnSection from "../common/SingleColumnSection";
import SectionTitle from "../common/SectionTitle";
import SequencesNewButton from "../sequences/SequencesNewButton";
import NewConversationButton from "../messaging/NewConversationButton";
import TagEditsByUser from "../tagging/TagEditsByUser";
import SettingsButton from "../icons/SettingsButton";
import { ContentItemBody } from "../contents/ContentItemBody";
import Loading from "../vulcan-core/Loading";
import PermanentRedirect from "../common/PermanentRedirect";
import { Typography } from "../common/Typography";
import ContentStyles from "../common/ContentStyles";
import ReportUserButton from "./ReportUserButton";
import UserNotifyDropdown from "../notifications/UserNotifyDropdown";
import CommentsSortBySelector from "../comments/CommentsSortBySelector";
import { StructuredData } from '../common/StructuredData';
import { useQuery } from "@/lib/crud/useQuery";
import { gql } from "@/lib/generated/gql-codegen";
import { StatusCodeSetter } from '../next/StatusCodeSetter';
import CommentsDraftList from '../comments/CommentsDraftList';
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const UsersProfileMultiQuery = gql(`
  query multiUserUsersProfileQuery($selector: UserSelector, $limit: Int, $enableTotal: Boolean) {
    users(selector: $selector, limit: $limit, enableTotal: $enableTotal) {
      results {
        ...UsersProfile
      }
      totalCount
    }
  }
`);

export const sectionFooterLeftStyles = {
  flexGrow: 1,
  display: "flex",
  '&&:after': {
    content: '""'
  }
}

const styles = defineStyles('UsersProfile', (theme: ThemeType) => ({
  profilePage: {
    marginLeft: "auto",
    [theme.breakpoints.down('sm')]: {
      paddingTop: 10,
      margin: 0,
    }
  },
  usernameTitle: {
    ...theme.typography.display3,
    ...theme.typography.headerStyle,
    marginTop: 0,
  },
  deletedUserName: {
    textDecoration: "line-through",
  },
  userInfo: {
    display: "flex",
    flexWrap: "wrap",
    color: theme.palette.lwTertiary.main,
    marginTop: 8,
    [theme.breakpoints.up('sm')]: {
      ...separatorBulletStyles(theme),
    },
    [theme.breakpoints.down('sm')]: {
      ...separatorBulletStyles(theme, 0.375),
    },
  },
  meta: {
    ...sectionFooterLeftStyles,
    [theme.breakpoints.down('sm')]: {
      width: "100%",
      marginBottom: 8,
    }
  },
  icon: {
    '&$specificalz': {
      fontSize: 18,
      color: theme.palette.icon.dim,
      marginRight: 4
    }
  },
  actions: {
    marginLeft: 20,
  },
  bio: {
    marginTop: 24,
  },
  postsTitle: {
    cursor: "pointer"
  },
  // Dark Magick
  // https://giphy.com/gifs/psychedelic-art-phazed-12GGadpt5aIUQE
  specificalz: {},
  userMetaInfo: {
    display: "inline-flex"
  },
  copyIcon: {
    fontSize: 14
  },
  subscribeButton: {
    display: "flex",
  },
  commentSorting: {
    marginRight: 30,
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
    },
  },
  dialogueButton: {
    display: 'flex',
    alignItems: 'center',
  },
}))

export const getUserFromResults = <T extends UsersMinimumInfo>(results: Array<T>|null|undefined): T|null => {
  // HOTFIX: Filtering out invalid users
  return results?.find(user => !!user.displayName) || results?.[0] || null
}

const UsersProfileFn = ({terms, slug}: {
  terms: UsersViewTerms,
  slug: string,
}) => {
  const { isAF, forumType } = useForumType();
  const classes = useStyles(styles);
  const [showSettings, setShowSettings] = useState(false);

  const currentUser = useCurrentUser();
  const { flash } = useMessages();
  
  const { view, limit, ...selectorTerms } = terms;
  const { data, loading } = useQuery(UsersProfileMultiQuery, {
    variables: {
      selector: { [view]: selectorTerms },
      limit: 10,
      enableTotal: false,
    },
    notifyOnNetworkStatusChange: true,
  });

  const results = data?.users?.results;
  const user = getUserFromResults(results)
  
  const { query } = useLocation()

  const { openDialog } = useDialog();

  const displaySequenceSection = (canEdit: boolean, user: UsersProfile) => {
    if (isAF) {
        return !!((canEdit && user.afSequenceDraftCount) || user.afSequenceCount) || !!(!canEdit && user.afSequenceCount)
    } else {
        return !!((canEdit && user.sequenceDraftCount) || user.sequenceCount) || !!(!canEdit && user.sequenceCount)
    }
  }

  const [restoreScrollPos, setRestoreScrollPos] = useState(-1);
  useEffect(() => {
    if (restoreScrollPos === -1) return;

    window.scrollTo({top: restoreScrollPos})
    setRestoreScrollPos(-1);
  }, [restoreScrollPos])

  const renderMeta = () => {
    if (!user) return null
    const { karma, postCount, commentCount, afPostCount, afCommentCount, afKarma, tagRevisionCount } = user;

    const userKarma = karma || 0
    const userAfKarma = afKarma || 0
    const userPostCount = !isAF ? postCount || 0 : afPostCount || 0
    const userCommentCount = !isAF ? commentCount || 0 : afCommentCount || 0

      return <div className={classes.meta}>

        { !isAF && <TooltipSpan title={`${userKarma} karma`} className={classes.userMetaInfo}>
          <StarIcon className={classNames(classes.icon, classes.specificalz)}/>
          <MetaInfo title="Karma">
            {userKarma}
          </MetaInfo>
        </TooltipSpan>}

        {!!userAfKarma && <TooltipSpan title={`${userAfKarma} karma${(!isAF) ? " on alignmentforum.org" : ""}`} className={classes.userMetaInfo}>
          <OmegaIcon className={classNames(classes.icon, classes.specificalz)}/>
          <MetaInfo title="Alignment Karma">
            {userAfKarma}
          </MetaInfo>
        </TooltipSpan>}

        <TooltipSpan title={`${userPostCount} posts`} className={classes.userMetaInfo}>
          <DescriptionIcon className={classNames(classes.icon, classes.specificalz)}/>
          <MetaInfo title="Posts">
            {userPostCount}
          </MetaInfo>
        </TooltipSpan>

        <TooltipSpan title={`${userCommentCount} comments`} className={classes.userMetaInfo}>
          <MessageIcon className={classNames(classes.icon, classes.specificalz)}/>
          <MetaInfo title="Comments">
            { userCommentCount }
          </MetaInfo>
        </TooltipSpan>

        <TooltipSpan title={`${tagRevisionCount||0} wikitag edit${tagRevisionCount === 1 ? '' : 's'}`} className={classes.userMetaInfo}>
          <PencilIcon className={classNames(classes.icon, classes.specificalz)}/>
          <MetaInfo>
            { tagRevisionCount||0 }
          </MetaInfo>
        </TooltipSpan>
      </div>
  }

  const render = () => {
    if (loading) {
      return <div className={classNames("page", "users-profile", classes.profilePage)}>
        <Loading/>
      </div>
    }

    if (!user || !user._id || (user.deleted && !currentUser?.isAdmin)) {
      return <Error404/>
    }

    if (slug !== user.slug && user.oldSlugs?.includes(slug) && !user.deleted) {
      return <PermanentRedirect url={userGetProfileUrlFromSlug(user.slug)} />
    }

    // Does this profile page belong to a banned-and-purged spam account?
    if (user.banned && user.deleteContent) {
      if (currentUser?._id === user._id) {
        // Logged-in spammer can see their own profile
      } else if (currentUser && userCanDo(currentUser, 'posts.moderate.all')) {
        // Admins and sunshines can see spammer's profile
      } else {
        // Anyone else gets a 404 here
        // eslint-disable-next-line no-console
        console.log(`Not rendering profile page for banned-and-purged account: ${user.displayName}`);
        return <Error404/>
      }
    }

    const unlistedTerms: PostsViewTerms = {view: "unlisted", userId: user._id, limit: 20}
    const afSubmissionTerms: PostsViewTerms = {view: "userAFSubmissions", userId: user._id, limit: 4}
    const postTerms: PostsViewTerms = {
      view: "userPosts",
      ...pick(query, postListSettingUrlParameterNames),
      userId: user._id,
      authorIsUnreviewed: null
    };
    const sequenceTerms: SequencesViewTerms = {view: "userProfile", userId: user._id, limit:9}
    const sequenceAllTerms: SequencesViewTerms = {view: "userProfileAll", userId: user._id, limit:9}

    // maintain backward compatibility with bookmarks
    const postQueryMode = (query.sortedBy || query.view ||  "new")
    const currentPostSortingMode = POST_SORTING_MODES.has(postQueryMode) ? postQueryMode : "new"
    postTerms.sortedBy = currentPostSortingMode
    
    const currentFilter = query.filter ||  "all"
    
    const commentQueryName = "commentsSortBy"
    const commentQueryMode = query[commentQueryName]
    const currentCommentSortBy = COMMENT_SORTING_MODES.has(commentQueryMode) ? commentQueryMode : undefined

    const ownPage = currentUser?._id === user._id
    const currentShowLowKarma = (parseInt(query.karmaThreshold) !== DEFAULT_LOW_KARMA_THRESHOLD)
    const currentIncludeEvents = (query.includeEvents === 'true')
    postTerms.excludeEvents = !currentIncludeEvents && currentFilter !== 'events'
    

    const username = userGetDisplayName(user, forumType)
    const metaDescription = `${username}'s profile on ${siteNameWithArticleSetting.get(forumType)} — ${taglineSetting.get(forumType)}`
    
    const nonAFMember = (isAF && !userCanDo(currentUser, "posts.alignment.new"))

    const showMessageButton = currentUser?._id !== user._id

    return (
      <div className={classNames("page", "users-profile", classes.profilePage)}>
        <StatusCodeSetter status={200}/>
        <StructuredData generate={() => getUserStructuredData(user, forumType)}/>
        <AnalyticsContext pageContext={"userPage"}>
          {/* Bio Section */}
          <SingleColumnSection>
            <div className={classNames(classes.usernameTitle, {
              [classes.deletedUserName]: user.deleted
            })}>
              {username}
            </div>
            {user.deleted && "(account deleted)"}
            <Typography variant="body2" className={classes.userInfo}>
              { renderMeta() }
              { currentUser?.isAdmin &&
                <div>
                  <LWTooltip title="Click to copy userId" placement="right">
                    <CopyToClipboard text={user._id} onCopy={()=>flash({messageString:"userId copied!"})}>
                      <CopyIcon className={classes.copyIcon} />
                    </CopyToClipboard>
                  </LWTooltip>
                </div>
              }
              { currentUser?.isAdmin && <NewFeedButton user={user} /> }
              { currentUser && currentUser._id === user._id && <Link to="/account?tab=subscriptions">
                Manage Subscriptions
              </Link>}
              { showMessageButton && <NewConversationButton user={user} currentUser={currentUser}>
                <a>Message</a>
              </NewConversationButton> }
              { showMessageButton && (
                <div
                  className={classes.subscribeButton}
                  onClick={() => openDialog({ 
                    name: "NewDialogueDialog", 
                    contents: ({onClose}) => <NewDialogueDialog
                      onClose={onClose}
                      initialParticipantIds={[user._id]}
                    />
                  })}
                >
                  <a>Dialogue</a>
                </div>
              )}
              { <UserNotifyDropdown 
                user={user} 
                popperPlacement="bottom-end"
                className={classes.subscribeButton} 
              /> }
              {userCanEditUser(currentUser, user) && <Link to={userGetEditUrl(user, currentUser)}>
                Account Settings
              </Link>}
            </Typography>

            {user.htmlBio && <ContentStyles contentType="post">
              <ContentItemBody className={classes.bio} dangerouslySetInnerHTML={{__html: user.htmlBio }} description={`user ${user._id} bio`} nofollow={(user.karma || 0) < nofollowKarmaThreshold.get(forumType)}/>
            </ContentStyles>}
          </SingleColumnSection>

          <SingleColumnSection>
            <SunshineNewUsersProfileInfo userId={user._id} />
          </SingleColumnSection>

          {/* Sequences Section */}
          { displaySequenceSection(ownPage, user) && <SingleColumnSection>
            <SectionTitle title="Sequences">
              {ownPage && <SequencesNewButton />}
            </SectionTitle>
            <SequencesGridWrapper
                terms={ownPage ? sequenceAllTerms : sequenceTerms}
                showLoadMore={true}/>
          </SingleColumnSection> }

          {/* Drafts Section */}
          { ownPage && <SingleColumnSection>
            <AnalyticsContext listContext={"userPageDrafts"}>
              <DraftsList limit={5}/>
              <PostsList2 hideAuthor showDraftTag={false} terms={unlistedTerms} showNoResults={false} showLoading={false} showLoadMore={false}/>
              <CommentsDraftList userId={user._id} initialLimit={5} sectionTitleStyle />
            </AnalyticsContext>
            {hasEventsSetting.get(forumType) && <LocalGroupsList
              view='userInactiveGroups'
              terms={{userId: currentUser?._id}}
              showNoResults={false}
            />}
          </SingleColumnSection> }
          {/* AF Submissions Section */}
          {ownPage && nonAFMember && <SingleColumnSection>
            <LWTooltip inlineBlock={false} title="Your posts are pending approval to the Alignment Forum and are only visible to you on the Forum. 
            They are visible to everyone on LessWrong.">
              <SectionTitle title="My Submissions"/>
            </LWTooltip>
            <PostsList2 hideAuthor showDraftTag={false} terms={afSubmissionTerms}/>
          </SingleColumnSection>
          }
          {/* Posts Section */}
          <SingleColumnSection>
            <div className={classes.postsTitle} onClick={() => setShowSettings(!showSettings)}>
              <SectionTitle title={"Posts"}>
                <SettingsButton label={`Sorted by ${ getSortOrderOptions()[currentPostSortingMode].label }`}/>
              </SectionTitle>
            </div>
            {showSettings && <PostsListSettings
              hidden={false}
              currentSorting={currentPostSortingMode}
              currentFilter={currentFilter}
              currentShowLowKarma={currentShowLowKarma}
              currentIncludeEvents={currentIncludeEvents}
            />}
            <AnalyticsContext listContext={"userPagePosts"}>
              {user.shortformFeedId && <ProfileShortform user={user}/>}
              <PostsList2 terms={postTerms} hideAuthor />
            </AnalyticsContext>
          </SingleColumnSection>
          {/* Groups Section */
            (ownPage || currentUser?.isAdmin) && <LocalGroupsList
              view='userActiveGroups'
              limit={300}
              terms={{userId: user._id}}
              heading="Organizer of"
              showNoResults={false}
            />
          }
          {/* Wiki Section */}
          <SingleColumnSection>
            <SectionTitle title="Wikitag Contributions" />
            <AnalyticsContext listContext={"userPageWiki"}>
              <TagEditsByUser
                userId={user._id}
                limit={10}
              />
            </AnalyticsContext>
          </SingleColumnSection>
          {/* Comments Sections */}
          <AnalyticsContext pageSectionContext="commentsSection">
            {ownPage && nonAFMember && <SingleColumnSection>
              <LWTooltip inlineBlock={false } title="Your comments are pending approval to the Alignment Forum and are only visible to you on the Forum. 
              They are visible to everyone on LessWrong.">
                <SectionTitle title={"Comment Submissions"} />
              </LWTooltip>
              <RecentComments selector={{ afSubmissions: { authorIsUnreviewed: null, userId: user._id } }} limit={5}/>
            </SingleColumnSection>}
            <SingleColumnSection>
              <SectionTitle title={<Link to={`${userGetProfileUrl(user)}/replies`}>Comments</Link>} rootClassName={classes.commentSorting}>
                <AnalyticsContext pageElementContext='userProfileCommentSort'>
                  Sorted by <CommentsSortBySelector setRestoreScrollPos={setRestoreScrollPos} />
                </AnalyticsContext>
              </SectionTitle>
              <RecentComments
                selector={{ profileComments: { sortBy: currentCommentSortBy, authorIsUnreviewed: null, userId: user._id } }}
                limit={10}
                showPinnedOnProfile
              />
            </SingleColumnSection>
          </AnalyticsContext>

          <ReportUserButton user={user}/>
        </AnalyticsContext>
      </div>
    )
  }

  return render();
}

export default UsersProfileFn;
